// src/components/publishing/connect-destination-modal.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  X, ArrowLeft, Loader2, CheckCircle2, ExternalLink, ChevronDown, ChevronUp, KeyRound, Globe, Linkedin, FileCode2,
} from "lucide-react";
import { toast } from "sonner";
import {
  publishingApi, Destination, destinationLabel,
  HashnodeCredentials, WordPressCredentials, LinkedInCredentials,
  AccountMetadata,
} from "@/app/services/publishing/publishing-api";
import { Banner } from "../kit";

interface ConnectDestinationModalProps {
  destination: Destination | null;
  brandName?: string;
  onClose: () => void;
  onConnected: () => void;
}

export function ConnectDestinationModal({
  destination, brandName, onClose, onConnected,
}: ConnectDestinationModalProps) {
  useEffect(() => {
    if (!destination) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [destination]);

  if (!destination) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
      />

      <div
        className="fixed right-0 top-0 bottom-0 z-50 overflow-y-auto shadow-2xl"
        style={{
          width: "min(720px, 100vw)",
          backgroundColor: "var(--background)",
          borderLeft: "1px solid var(--border)",
          animation: "slideInRight 0.3s ease-out",
        }}
      >
        <Header destination={destination} onClose={onClose} />

        <div className="px-6 md:px-8 py-6">
          {destination === "hashnode" && (
            <HashnodeForm brandName={brandName} onClose={onClose} onConnected={onConnected} />
          )}
          {destination === "wordpress" && (
            <WordPressForm brandName={brandName} onClose={onClose} onConnected={onConnected} />
          )}
          {destination === "linkedin" && (
            <LinkedInForm brandName={brandName} onClose={onClose} onConnected={onConnected} />
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

function Header({ destination, onClose }: { destination: Destination; onClose: () => void }) {
  const meta = {
    hashnode:  { icon: FileCode2, color: "var(--a-info-t)", blurb: "Publish articles to your Hashnode blog." },
    wordpress: { icon: Globe,     color: "var(--a-brand-t)", blurb: "Publish posts to your WordPress site — self-hosted or managed." },
    linkedin:  { icon: Linkedin,  color: "var(--a-reason-t)", blurb: "Post to your LinkedIn profile or Company Page." },
  }[destination];
  const Icon = meta.icon;

  return (
    <div
      className="sticky top-0 z-10 px-6 md:px-8 py-5 border-b"
      style={{ backgroundColor: "var(--background)", borderColor: "var(--border)" }}
    >
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onClose}
          className="flex items-center gap-2"
          style={{
            color: "var(--muted-foreground)", background: "none", border: "none",
            cursor: "pointer", fontSize: 13, fontWeight: 600,
          }}
        >
          <ArrowLeft style={{ width: 14, height: 14 }} />
          Back to Integrations
        </button>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            padding: 6, borderRadius: 7, border: "none", backgroundColor: "transparent",
            cursor: "pointer", color: "var(--muted-foreground)",
          }}
        >
          <X style={{ width: 18, height: 18 }} />
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div
          style={{
            width: 48, height: 48, borderRadius: 11,
            backgroundColor: "var(--muted)",
            border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon style={{ width: 24, height: 24, color: meta.color }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--foreground)", marginBottom: 4 }}>
            Connect {destinationLabel(destination)}
          </h2>
          <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.55 }}>
            {meta.blurb}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Accordion primitive ─────────────────────────────────────────────────────

function Step({
  number, title, children, defaultOpen,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 10,
        marginBottom: 10,
        backgroundColor: "var(--muted)",
        overflow: "hidden",
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 12,
          padding: "12px 14px",
          border: "none", backgroundColor: "transparent", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div
          style={{
            width: 26, height: 26, borderRadius: 999,
            backgroundColor: "var(--a-brand-sub)",
            border: "1px solid var(--a-brand-line)",
            color: "var(--a-brand-t)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}
        >
          {number}
        </div>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
          {title}
        </span>
        {open
          ? <ChevronUp   style={{ width: 15, height: 15, color: "var(--muted-foreground)" }} />
          : <ChevronDown style={{ width: 15, height: 15, color: "var(--muted-foreground)" }} />
        }
      </button>
      {open && (
        <div style={{
          padding: "4px 14px 14px 52px",
          fontSize: 12, color: "var(--foreground)", lineHeight: 1.6,
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Shared form primitives ───────────────────────────────────────────────────

const inputSt: React.CSSProperties = {
  width: "100%", padding: "9px 12px", borderRadius: 8,
  border: "1px solid var(--border)", backgroundColor: "var(--background)",
  fontSize: 13, color: "var(--foreground)",
  outline: "none", boxSizing: "border-box",
  fontFamily: "inherit",
};

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: 11, fontWeight: 700, color: "var(--muted-foreground)",
      textTransform: "uppercase", letterSpacing: "0.05em",
      marginBottom: 6, margin: "0 0 6px 0",
    }}>
      {children}
    </p>
  );
}

function DocsLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        fontSize: 12, color: "var(--a-brand-t)", textDecoration: "none", fontWeight: 600,
      }}
    >
      {children}
      <ExternalLink style={{ width: 11, height: 11 }} />
    </a>
  );
}

function PrimaryButton({
  onClick, disabled, loading, children,
}: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
        padding: "9px 18px", borderRadius: 8,
        border: "none",
        backgroundColor: (disabled || loading) ? "var(--border)" : "var(--a-brand)",
        color: "var(--a-brand-on)",
        cursor: (disabled || loading) ? "not-allowed" : "pointer",
        fontSize: 13, fontWeight: 600,
      }}
    >
      {loading && <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />}
      {children}
    </button>
  );
}

function SecondaryButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "9px 14px", borderRadius: 8,
        border: "1px solid var(--border)", backgroundColor: "var(--muted)",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 13, fontWeight: 600, color: "var(--foreground)",
      }}
    >
      {children}
    </button>
  );
}

// ─── Verified account preview ────────────────────────────────────────────────

function VerifiedBanner({ meta }: { meta: AccountMetadata }) {
  const parts: string[] = [];
  if (meta.display_name) parts.push(meta.display_name);
  if (meta.username && meta.username !== meta.display_name) parts.push(`@${meta.username}`);
  if (meta.publication_title) parts.push(`→ ${meta.publication_title}`);
  if (meta.site_url) parts.push(meta.site_url);

  const warning = meta.publication_not_found;

  return (
    <div
      style={{
        marginTop: 14, padding: 12, borderRadius: 9,
        backgroundColor: warning ? "var(--st-warn-bg)" : "var(--st-ok-bg)",
        border: `1px solid ${warning ? "var(--st-warn-bd)" : "var(--st-ok-bg)"}`,
        display: "flex", alignItems: "flex-start", gap: 10,
      }}
    >
      <CheckCircle2 style={{
        width: 17, height: 17,
        color: warning ? "var(--st-warn-t)" : "var(--st-ok-t)",
        flexShrink: 0, marginTop: 1,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: warning ? "var(--st-warn-t)" : "var(--st-ok-t)" }}>
          Verified
        </div>
        <div style={{ fontSize: 12, color: "var(--foreground)", overflowWrap: "anywhere", wordBreak: "break-word" }}>
          {parts.join(" · ")}
          {warning && (
            <div style={{ fontSize: 11, color: "var(--st-warn-t)", marginTop: 4 }}>
              {warning}
            </div>
          )}
          {meta.note && (
            <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
              {meta.note}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  Hashnode form (Option B — guided accordion with step-by-step)
// ═══════════════════════════════════════════════════════════════════════════

function HashnodeForm({
  brandName, onClose, onConnected,
}: {
  brandName?: string;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [pat, setPat] = useState("");
  const [pubHost, setPubHost] = useState("");
  const [verified, setVerified] = useState<AccountMetadata | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canTest = pat.trim().length >= 10;

  const onTest = async () => {
    if (!canTest) { setError("Paste your personal access token first."); return; }
    setError(null); setTesting(true); setVerified(null);
    try {
      const res = await publishingApi.testConnection({
        destination: "hashnode",
        hashnode: {
          personal_access_token: pat.trim(),
          publication_host: pubHost.trim() || null,
        },
      });
      if (res.ok) {
        setVerified(res.account_metadata);
      } else {
        setError(res.error || "Could not verify credentials.");
      }
    } catch (e: any) {
      setError(e.message || "Could not verify credentials.");
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    if (!verified) { setError("Verify your token first."); return; }
    if (!verified.publication_id) {
      setError("No publication found. Enter your blog URL above (e.g. myblog.hashnode.dev) and verify again — we need a publication to post to.");
      return;
    }
    setError(null); setSaving(true);
    try {
      await publishingApi.connect({
        destination: "hashnode",
        brand_name: brandName,
        hashnode: {
          personal_access_token: pat.trim(),
          publication_host: pubHost.trim() || null,
        },
      });
      toast.success("Hashnode connected");
      onConnected();
      onClose();
    } catch (e: any) {
      setError(e.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--foreground)", marginBottom: 10 }}>
        How to get your Hashnode personal access token
      </h3>

      <Step number={1} title="Create a Hashnode blog (if you don't have one)" defaultOpen>
        <p>
          If you don't already have a Hashnode blog, create one at Hashnode.com
          — it's free. You'll get a subdomain like{" "}
          <code style={codeChip}>yourname.hashnode.dev</code> or can connect
          your own custom domain.
        </p>
        <div style={{ marginTop: 8 }}>
          <DocsLink href="https://hashnode.com/onboard/blog">
            Create a Hashnode blog
          </DocsLink>
        </div>
      </Step>

      <Step number={2} title="Generate a Personal Access Token">
        <p>
          Open Hashnode's developer settings. You'll need to be signed in.
          Click <strong>Generate a new token</strong>, give it a name like
          "Upmode Brand Publisher", and copy the token.
        </p>
        <p style={{ marginTop: 6, color: "var(--muted-foreground)" }}>
          Hashnode shows the token once. Copy it immediately — you won't be
          able to see it again. The token never expires unless you revoke it.
        </p>
        <div style={{ marginTop: 8 }}>
          <DocsLink href="https://hashnode.com/settings/developer">
            Open hashnode.com/settings/developer
          </DocsLink>
        </div>
      </Step>

      <Step number={3} title="Find your blog's URL">
        <p>
          We post to a specific publication, so we need to know which blog
          of yours. Copy the domain of your Hashnode blog — it looks like:
        </p>
        <ul style={{ margin: "6px 0 6px 18px", padding: 0 }}>
          <li><code style={codeChip}>yourname.hashnode.dev</code> (free subdomain), or</li>
          <li><code style={codeChip}>blog.yourdomain.com</code> (custom domain)</li>
        </ul>
        <p>
          Paste just the domain — no <code style={codeChip}>https://</code> and
          no trailing slash.
        </p>
      </Step>

      <Step number={4} title="Paste the details below and verify">
        <p>
          Paste both into the fields below and click <strong>Verify</strong>.
          Upmode will call Hashnode's API to confirm the token works and
          resolve your publication so we know where to post.
        </p>
      </Step>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <Label>Hashnode personal access token</Label>
          <input
            type="password"
            value={pat}
            onChange={e => setPat(e.target.value)}
            placeholder="Paste your token here"
            style={inputSt}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div>
          <Label>Blog URL</Label>
          <input
            type="text"
            value={pubHost}
            onChange={e => setPubHost(e.target.value)}
            placeholder="yourname.hashnode.dev"
            style={{ ...inputSt, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
            autoComplete="off"
            spellCheck={false}
          />
          <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
            The domain of the Hashnode blog you want to publish to.
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-3">
          <Banner tone="error">
              <span className="upm-lead">Connection failed</span>
            {error}
          </Banner>
        </div>
      )}

      {verified && <VerifiedBanner meta={verified} />}

      <div style={{
        display: "flex", gap: 10, justifyContent: "flex-end",
        marginTop: 20, flexWrap: "wrap",
      }}>
        <SecondaryButton onClick={onClose} disabled={testing || saving}>Cancel</SecondaryButton>
        {!verified ? (
          <PrimaryButton onClick={onTest} loading={testing} disabled={!canTest}>
            <KeyRound style={{ width: 13, height: 13 }} />
            Verify token
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={onSave} loading={saving}>
            Save & connect
          </PrimaryButton>
        )}
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  WordPress form
// ═══════════════════════════════════════════════════════════════════════════

function WordPressForm({
  brandName, onClose, onConnected,
}: {
  brandName?: string;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [siteUrl, setSiteUrl] = useState("");
  const [username, setUsername] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [verified, setVerified] = useState<AccountMetadata | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canTest = siteUrl.trim().length > 6 && username.trim().length > 0 && appPassword.trim().length > 0;

  const onTest = async () => {
    setError(null); setTesting(true); setVerified(null);
    try {
      const res = await publishingApi.testConnection({
        destination: "wordpress",
        wordpress: {
          site_url: siteUrl.trim(),
          username: username.trim(),
          application_password: appPassword.trim(),
        },
      });
      if (res.ok) {
        if (res.account_metadata?.capabilities_can_publish === false) {
          setError("This user can connect but does not have permission to publish posts on this site.");
        } else {
          setVerified(res.account_metadata);
        }
      } else {
        setError(res.error || "Could not verify credentials.");
      }
    } catch (e: any) {
      setError(e.message || "Could not verify credentials.");
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    if (!verified) { setError("Verify your credentials first."); return; }
    setError(null); setSaving(true);
    try {
      await publishingApi.connect({
        destination: "wordpress",
        brand_name: brandName,
        wordpress: {
          site_url: siteUrl.trim(),
          username: username.trim(),
          application_password: appPassword.trim(),
        },
      });
      toast.success("WordPress connected");
      onConnected();
      onClose();
    } catch (e: any) {
      setError(e.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--foreground)", marginBottom: 10 }}>
        How to create a WordPress application password
      </h3>

      <Step number={1} title="Sign into WordPress admin" defaultOpen>
        <p>
          Open your site's admin dashboard. This is usually at
          <code style={codeChip}>yoursite.com/wp-admin</code>. The user you
          sign in as must have permission to publish posts (Author, Editor, or
          Administrator).
        </p>
      </Step>

      <Step number={2} title="Go to Users → Profile">
        <p>
          In the left sidebar, click <strong>Users</strong>, then your own
          profile (or <strong>Profile</strong> at the top).
        </p>
      </Step>

      <Step number={3} title="Scroll to 'Application Passwords'">
        <p>
          At the bottom of your profile page, find the <strong>Application Passwords </strong>
          section. Enter a name like "Upmode Brand Publisher" and click
          <strong> Add New Application Password</strong>.
        </p>
        <p style={{ marginTop: 6, color: "var(--muted-foreground)" }}>
          WordPress will show the password once, formatted as four-letter groups
          separated by spaces. Copy it now — you can't view it again.
        </p>
        <div style={{ marginTop: 8 }}>
          <DocsLink href="https://make.wordpress.org/core/2020/11/05/application-passwords-integration-guide/">
            Read WordPress's official guide
          </DocsLink>
        </div>
      </Step>

      <Step number={4} title="Paste the details below and verify">
        <p>
          Paste the site URL, your WordPress username (not email), and the
          application password. Upmode will do a test call to your site to
          confirm everything works.
        </p>
      </Step>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <Label>Site URL</Label>
          <input
            type="text"
            value={siteUrl}
            onChange={e => setSiteUrl(e.target.value)}
            placeholder="https://yourblog.example.com"
            style={inputSt}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div>
          <Label>Username</Label>
          <input
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="your WordPress login (not email)"
            style={inputSt}
            autoComplete="off"
          />
        </div>
        <div>
          <Label>Application password</Label>
          <input
            type="password"
            value={appPassword}
            onChange={e => setAppPassword(e.target.value)}
            placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
            style={inputSt}
            autoComplete="off"
            spellCheck={false}
          />
          <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
            Spaces are fine — we strip them before sending.
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-3">
          <Banner tone="error">
              <span className="upm-lead">Connection failed</span>
            {error}
          </Banner>
        </div>
      )}

      {verified && <VerifiedBanner meta={verified} />}

      <div style={{
        display: "flex", gap: 10, justifyContent: "flex-end",
        marginTop: 20, flexWrap: "wrap",
      }}>
        <SecondaryButton onClick={onClose} disabled={testing || saving}>Cancel</SecondaryButton>
        {!verified ? (
          <PrimaryButton onClick={onTest} loading={testing} disabled={!canTest}>
            Verify credentials
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={onSave} loading={saving}>
            Save & connect
          </PrimaryButton>
        )}
      </div>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
//  LinkedIn form (Option B — guided accordion with step-by-step)
// ═══════════════════════════════════════════════════════════════════════════

function LinkedInForm({
  brandName, onClose, onConnected,
}: {
  brandName?: string;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [accessToken, setAccessToken] = useState("");
  const [authorUrn, setAuthorUrn] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [verified, setVerified] = useState<AccountMetadata | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const urnType = useMemo(() => {
    if (authorUrn.startsWith("urn:li:organization:")) return "organization";
    if (authorUrn.startsWith("urn:li:person:")) return "person";
    return null;
  }, [authorUrn]);

  const canTest = accessToken.trim().length > 20 && authorUrn.trim().length > 10 && urnType !== null;

  const onTest = async () => {
    setError(null); setTesting(true); setVerified(null);
    try {
      const res = await publishingApi.testConnection({
        destination: "linkedin",
        linkedin: {
          access_token: accessToken.trim(),
          author_urn: authorUrn.trim(),
        },
      });
      if (res.ok) {
        setVerified(res.account_metadata);
      } else {
        setError(res.error || "Could not verify token.");
      }
    } catch (e: any) {
      setError(e.message || "Could not verify token.");
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    if (!verified) { setError("Verify your token first."); return; }
    setError(null); setSaving(true);
    try {
      await publishingApi.connect({
        destination: "linkedin",
        brand_name: brandName,
        linkedin: {
          access_token: accessToken.trim(),
          author_urn: authorUrn.trim(),
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        },
      });
      toast.success("LinkedIn connected");
      onConnected();
      onClose();
    } catch (e: any) {
      setError(e.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        style={{
          padding: "10px 12px", borderRadius: 9,
          backgroundColor: "var(--st-warn-bg)",
          border: "1px solid var(--st-warn-bd)",
          fontSize: 12, color: "var(--foreground)", lineHeight: 1.6,
          marginBottom: 16,
        }}
      >
        <strong style={{ color: "var(--st-warn-t)" }}>Heads up:</strong> LinkedIn requires
        you to create your own developer app to get a token. This takes ~10
        minutes the first time. The token will expire after 60 days (or 365
        days if you request a long-lived one), and you'll need to reconnect.
      </div>

      <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--foreground)", marginBottom: 10 }}>
        How to get your LinkedIn access token
      </h3>

      <Step number={1} title="Create a LinkedIn developer app" defaultOpen>
        <p>
          Sign into LinkedIn, then open the Developer Portal and create a new
          app. You'll need to link it to a Company Page you admin (use your own
          brand's page).
        </p>
        <div style={{ marginTop: 8 }}>
          <DocsLink href="https://www.linkedin.com/developers/apps/new">
            Open LinkedIn Developer Portal
          </DocsLink>
        </div>
      </Step>

      <Step number={2} title="Request the 'Share on LinkedIn' product">
        <p>
          In your app, go to the <strong>Products</strong> tab. Request the
          <strong> Share on LinkedIn</strong> product. This is auto-approved
          and grants the
          <code style={codeChip}>w_member_social</code>
          scope so you can post as yourself.
        </p>
        <p style={{ marginTop: 8, color: "var(--muted-foreground)" }}>
          Optional: to post as a <strong>Company Page</strong> (not yourself),
          also request <strong>Marketing Developer Platform</strong>. LinkedIn
          reviews this manually and can take 1-3 weeks.
        </p>
      </Step>

      <Step number={3} title="Generate an OAuth 2.0 access token">
        <p>
          Go to the <strong>Auth</strong> tab in your app. Under <strong>OAuth 2.0
          tools</strong>, click <strong>Generate token</strong>. Select at
          minimum:
        </p>
        <ul style={{ margin: "6px 0 6px 18px", paddingLeft: 0 }}>
          <li><code style={codeChip}>openid</code></li>
          <li><code style={codeChip}>profile</code></li>
          <li><code style={codeChip}>w_member_social</code> — lets you post as yourself</li>
          <li>
            <code style={codeChip}>w_organization_social</code> — only if you
            got Marketing Developer Platform and want to post as the Company
            Page
          </li>
        </ul>
        <p>
          LinkedIn will show you the access token. Copy it — you cannot view it
          again.
        </p>
        <div style={{ marginTop: 8 }}>
          <DocsLink href="https://learn.microsoft.com/en-us/linkedin/shared/authentication/authorization-code-flow">
            LinkedIn docs: OAuth 2.0
          </DocsLink>
        </div>
      </Step>

      <Step number={4} title="Find your Author URN">
        <p>
          An <strong>Author URN</strong> identifies who will be listed as the
          author of the post.
        </p>
        <p style={{ marginTop: 6 }}>
          <strong>To post as yourself:</strong> open any of your LinkedIn posts,
          right-click → Inspect. Search the page source for{" "}
          <code style={codeChip}>urn:li:person:</code> — the value that follows
          is your personal URN. Paste the full string.
        </p>
        <p style={{ marginTop: 6 }}>
          <strong>To post as a Company Page:</strong> go to your app's{" "}
          <strong>Settings</strong> tab. Scroll to{" "}
          <strong>Company associated with this app</strong>. The ID at the end
          of the company URL (e.g. <code style={codeChip}>12345678</code> from{" "}
          <code style={codeChip}>linkedin.com/company/12345678/</code>) is your
          organization ID. The URN is{" "}
          <code style={codeChip}>urn:li:organization:12345678</code>.
        </p>
      </Step>

      <Step number={5} title="Paste your token and URN below">
        <p>
          Click <strong>Verify</strong> to test the pair. Optionally set the
          expiry date (LinkedIn shows this on the Auth tab after generating the
          token) so we can warn you when it's about to expire.
        </p>
      </Step>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <Label>LinkedIn access token</Label>
          <textarea
            value={accessToken}
            onChange={e => setAccessToken(e.target.value)}
            placeholder="Paste token here"
            rows={3}
            style={{ ...inputSt, resize: "vertical", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div>
          <Label>Author URN</Label>
          <input
            type="text"
            value={authorUrn}
            onChange={e => setAuthorUrn(e.target.value)}
            placeholder="urn:li:person:XXXXX or urn:li:organization:YYYYY"
            style={{ ...inputSt, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
            autoComplete="off"
            spellCheck={false}
          />
          {urnType && (
            <p style={{ fontSize: 11, color: "var(--a-brand-t)", marginTop: 4, fontWeight: 600 }}>
              Will post as a {urnType === "organization" ? "Company Page" : "personal profile"}.
            </p>
          )}
        </div>
        <div>
          <Label>Token expiry date (optional)</Label>
          <input
            type="date"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
            style={inputSt}
          />
          <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
            If set, we'll warn you when the token has fewer than 7 days remaining.
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-3">
          <Banner tone="error">
              <span className="upm-lead">Connection failed</span>
            {error}
          </Banner>
        </div>
      )}

      {verified && <VerifiedBanner meta={verified} />}

      <div style={{
        display: "flex", gap: 10, justifyContent: "flex-end",
        marginTop: 20, flexWrap: "wrap",
      }}>
        <SecondaryButton onClick={onClose} disabled={testing || saving}>Cancel</SecondaryButton>
        {!verified ? (
          <PrimaryButton onClick={onTest} loading={testing} disabled={!canTest}>
            <KeyRound style={{ width: 13, height: 13 }} />
            Verify token
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={onSave} loading={saving}>
            Save & connect
          </PrimaryButton>
        )}
      </div>
    </>
  );
}

const codeChip: React.CSSProperties = {
  padding: "1px 6px",
  margin: "0 3px",
  borderRadius: 4,
  backgroundColor: "var(--background)",
  border: "1px solid var(--border)",
  fontSize: 11,
  fontFamily: "ui-monospace, SFMono-Regular, monospace",
};